/**
 * The Content section's glossary: one canonical label per concept (the technical term is kept,
 * so operators and engineers say the same word) and a one-line plain meaning for its ⓘ tooltip.
 * Every page reads labels from here; enum value labels for grounding, category resolution, QC
 * severity and run-product status live here too, so one value never reads two ways.
 */

/** One concept: the label the UI shows and the plain meaning behind its ⓘ (≤ 120 chars). */
export interface GlossaryEntry {
  label: string;
  meaning: string;
}

/** Canonical concepts, keyed by a stable code. */
export const GLOSSARY = {
  typeAndAttributes: {
    label: 'Product group & attributes',
    meaning:
      'What the AI read the product as: its product group (detection) and attribute values (extraction).',
  },
  productType: {
    label: 'Product group',
    meaning:
      'The product group the AI detected (e.g. Dresses). It picks which attributes are asked.',
  },
  attribute: {
    label: 'Attribute',
    meaning:
      'One thing about the product the AI is asked to read from its feed text and images, e.g. Colour.',
  },
  value: {
    label: 'Value',
    meaning: 'What the AI read from the product’s feed text and images, before matching to a tag.',
  },
  tag: {
    label: 'Tag',
    meaning: 'The vocabulary word the value is saved as, e.g. "navy blue" is saved as Navy.',
  },
  vocabulary: {
    label: 'Vocabulary',
    meaning: 'Your allowed tags per product group and attribute.',
  },
  grounding: {
    label: 'Grounding',
    meaning:
      'How the value matched a vocabulary tag: Exact match, Synonym, Nearest meaning, No tag or Not answered.',
  },
  confidence: {
    label: 'Confidence',
    meaning: 'How sure the AI was, 0–1. 0.85+ is high; below 0.70 is low and worth a check.',
  },
  categoryResolution: {
    label: 'Category resolution',
    meaning: 'How your feed category was mapped to a Google category that has a vocabulary.',
  },
  qcCheck: {
    label: 'QC check',
    meaning:
      'A run that checks the AI’s product groups and attributes with QC rules, then opens them for Review.',
  },
  qcRule: {
    label: 'QC rule',
    meaning:
      'An automatic rule run on the AI’s answers after it replies, e.g. “Claim not in the product text”.',
  },
  scoringRule: {
    label: 'Scoring rule',
    meaning: 'One of the channel’s content rules. A failing scoring rule costs the product points.',
  },
  problem: {
    label: 'Problem',
    meaning:
      'Something that cost a product points or stopped it being written, with what can fix it.',
  },
  qcFlag: {
    label: 'QC flag',
    meaning:
      'Something a QC rule found worth a look. It doesn’t change the score — judge it in Review.',
  },
  severity: {
    label: 'Severity',
    meaning:
      'High: likely wrong. Medium: worth a look. Suggestion: optional. Info: for the record.',
  },
  noMatch: {
    label: 'No match',
    meaning: 'No product group fitted, so the General instructions are used.',
  },
  notDetected: {
    label: 'Not detected',
    meaning: 'Product group detection hasn’t run on this product yet.',
  },
  generalInstructions: {
    label: 'General instructions',
    meaning: 'The instructions used for products whose product group is No match.',
  },
  instructions: {
    label: 'Instructions',
    meaning:
      'What the AI was told to do. “Lily default · v7” is Lily’s version 7; “Custom” is your workspace’s own.',
  },
  soldAs: {
    label: 'Sold as',
    meaning:
      'How the product is sold: a single product, a set of different pieces, or a multipack of one.',
  },
  setPieces: {
    label: 'Pieces of the set',
    meaning: 'For a set or multipack: its kind, pack quantity, the pieces and who it’s for.',
  },
  reused: {
    label: 'Reused',
    meaning:
      'Taken from an earlier run, until the product text or the instructions version changes.',
  },
  batch: {
    label: 'Batch',
    meaning: 'A run processes products in groups (batches); this is the group the product was in.',
  },
  score: {
    label: 'Score',
    meaning:
      'Content quality 0–100 against the channel’s rules. Before = feed text, after = written.',
  },
  verdict: {
    label: 'Verdict',
    meaning:
      'From the score: Pass (90+), Needs review (70–90), Fail (below 70 or a critical problem).',
  },
  gates: {
    label: 'Gates',
    meaning:
      'Targets a QC check must meet (e.g. average score 80+) before its results count as good.',
  },
  decision: {
    label: 'Decision',
    meaning:
      'A reviewer’s call on an AI answer or QC flag: Correct, Wrong, Added, or Real issue / Not an issue.',
  },
  productStatus: {
    label: 'Status',
    meaning:
      'What the run did with this product: Written, Partial, Failed, Not attempted or Skipped.',
  },
  fields: {
    label: 'Fields',
    meaning: 'The written content parts, e.g. title, description and bullets.',
  },
  channel: {
    label: 'Channel',
    meaning: 'Where the content is published, e.g. Google Shopping. Scores use its rules.',
  },
  gapCheck: {
    label: 'Gap check',
    meaning:
      'Compares written content with the feed. Reads only — nothing changes — one AI call per product.',
  },
} as const satisfies Record<string, GlossaryEntry>;

/** A concept's code in the glossary. */
export type GlossaryTerm = keyof typeof GLOSSARY;

/**
 * Grounding rungs (`CoreAttributeRead.grounding`): one label set app-wide — the service's names
 * map to Exact match / Synonym / Nearest meaning / No tag / Not answered.
 */
export const GROUNDING_GLOSSARY: Record<string, GlossaryEntry> = {
  EXACT: {
    label: 'Exact match',
    meaning: 'The value is word-for-word one of your vocabulary tags.',
  },
  SYNONYM: {
    label: 'Synonym',
    meaning: 'A known other way of saying one of your tags — e.g. “navy blue” is saved as Navy.',
  },
  VECTOR: {
    label: 'Nearest meaning',
    meaning:
      'No exact or known match, so the tag closest in meaning was used. Worth a quick check.',
  },
  REJECTED: {
    label: 'No tag',
    meaning: 'A real value your vocabulary has no tag for yet. Not an error — a candidate to add.',
  },
  NONE: {
    label: 'Not answered',
    meaning: 'The AI looked for this attribute but the product text didn’t mention it.',
  },
};

/** `categoryResolution` values: how the merchant's category was resolved. */
export const CATEGORY_RESOLUTION_GLOSSARY: Record<string, GlossaryEntry> = {
  EXACT: {
    label: 'Your category',
    meaning: 'Your own category was used as-is — it has a vocabulary.',
  },
  LADDER: {
    label: 'Broader category',
    meaning:
      'Moved up to a broader Google category that has a vocabulary (e.g. Maxi Dresses → Dresses).',
  },
  VALID_NO_SLICE: {
    label: 'No vocabulary yet',
    meaning: 'A real Google category with no vocabulary yet, so fewer attributes can be found.',
  },
  INVALID: {
    label: 'Category not recognised',
    meaning: 'The category you sent isn’t a Google category, so it was ignored.',
  },
  ABSENT: {
    label: 'No category sent',
    meaning: 'You sent no category for this product, so the AI placed it.',
  },
  MODEL_REJECTED: {
    label: 'AI category not allowed',
    meaning: 'The AI suggested a category that isn’t allowed, so the product has no category.',
  },
};

/** `resolvedBy` values: who reached the product group — a free lookup or a paid AI call. */
export const RESOLVED_BY_GLOSSARY: Record<string, GlossaryEntry> = {
  EXACT: {
    label: 'Your category',
    meaning: 'Your own category already had a vocabulary, so it was used directly — no AI needed.',
  },
  LADDER: {
    label: 'Broader category',
    meaning: 'Moved up from your category to a broader one with a vocabulary — no AI needed.',
  },
  MODEL: {
    label: 'AI decided',
    meaning:
      'The AI decided: your category was missing or unusable, or the product group needed judgement.',
  },
  DEFAULTED: {
    label: 'Default used',
    meaning: 'The AI gave no usable answer, so the default (No match) was saved.',
  },
  CACHED: {
    label: 'Reused',
    meaning: 'An identical product was already detected, so that answer was reused without the AI.',
  },
};

/** QC severities, most severe first. */
export const QC_SEVERITY_GLOSSARY = {
  HIGH: { label: 'High', meaning: 'Likely wrong — judge it in Review.' },
  MEDIUM: { label: 'Medium', meaning: 'Worth a look.' },
  SUGGESTION: { label: 'Suggestion', meaning: 'Optional improvement; nothing is wrong.' },
  INFO: { label: 'Info', meaning: 'For the record only.' },
} satisfies Record<string, GlossaryEntry>;

/** QC rules (`checkName`): the rule's name and what it looks for. */
export const QC_CHECK_GLOSSARY: Record<string, GlossaryEntry> = {
  UNSUPPORTED_CLAIM: {
    label: 'Claim not in the product text',
    meaning: 'The AI stated something the product’s feed text doesn’t support.',
  },
  INPUT_CONFLICT: {
    label: 'Conflicts with the product text',
    meaning: 'The value disagrees with what the product’s feed text says.',
  },
  CONTRADICTION: {
    label: 'Contradicting values',
    meaning: 'Two of the product’s answers contradict each other.',
  },
  FEED_MISMATCH: {
    label: 'Disagrees with your feed',
    meaning: 'The value differs from the one in your feed.',
  },
  VOCABULARY_GAP: {
    label: 'No tag',
    meaning: 'A real value your vocabulary has no tag for yet — a candidate to add.',
  },
  POSSIBLY_MISSED: {
    label: 'Possibly missed',
    meaning: 'The product text names this attribute, but the AI left it empty.',
  },
  DETECTION_DOUBT: {
    label: 'Product group in doubt',
    meaning: 'QC doubts the detected product group — check it in Review.',
  },
  LISTING_SHAPE_MISMATCH: {
    label: 'Set / multipack mismatch',
    meaning: 'The Sold as answer (single, set, multipack) disagrees with the product text.',
  },
  MISSING_PIECE: {
    label: 'Missing piece',
    meaning: 'The product text names a piece of the set that wasn’t picked up.',
  },
  PACK_COLOURS: {
    label: 'Pack colours',
    meaning: 'The colours given for a multipack’s pieces don’t match the product text.',
  },
  NO_ANSWERS: {
    label: 'Nothing found',
    meaning: 'The AI answered none of the asked attributes.',
  },
};

/** Review decision values (`ReviewEventRead.verdict`): a reviewer's call on an answer or QC flag. */
export const DECISION_GLOSSARY = {
  CORRECT: { label: 'Correct', meaning: 'The reviewer agreed with the AI’s answer.' },
  WRONG: {
    label: 'Wrong',
    meaning: 'The reviewer said the answer is wrong, often with the right value.',
  },
  ADDED: { label: 'Added', meaning: 'The reviewer added a value the AI left out.' },
  FINDING_CONFIRMED: { label: 'Real issue', meaning: 'The reviewer agreed with the QC flag.' },
  FINDING_DISMISSED: { label: 'Not an issue', meaning: 'The reviewer dismissed the QC flag.' },
  WITHDRAWN: { label: 'Withdrawn', meaning: 'The reviewer withdrew an earlier decision.' },
} satisfies Record<string, GlossaryEntry>;

/** `RunProductStatus` values: what a run did with one product. */
export const RUN_PRODUCT_STATUS_GLOSSARY: Record<string, GlossaryEntry> = {
  ENRICHED: { label: 'Written', meaning: 'Every asked field was written.' },
  PARTIAL: { label: 'Partial', meaning: 'Some asked fields were written, others weren’t.' },
  FAILED: { label: 'Failed', meaning: 'The run tried but wrote nothing for this product.' },
  NOT_ATTEMPTED: {
    label: 'Not attempted',
    meaning: 'Its batch closed (failed, skipped or cancelled) before the product was reached.',
  },
  NOT_REQUESTED: { label: 'Writing not requested', meaning: 'This run didn’t write content.' },
  SKIPPED_NOT_SELECTED: {
    label: 'Skipped · product group not chosen',
    meaning: 'Its product group wasn’t picked for writing in this run.',
  },
  SKIPPED_NO_PROMPT: {
    label: 'Skipped · no instructions',
    meaning: 'No instructions exist for its product group, so nothing was written.',
  },
};
